import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { observer } from 'mobx-react';
import { Tabs, Tab } from '@material-ui/core';
import AddressList from './common/AddressList';
import { useStores } from '../stores';
import Image from './common/Image';
import appStyles from '../../../config/appStyles.js';
import {
  isPickupPointAddress,
  isPickupPointCountry,
} from '../../../utils/pickupPoint';
import Progress from './common/Progress';
import usePickupPoints from './common/usePickupPoints';
import { toJS } from 'mobx';

const useStyles = makeStyles((theme) => appStyles.myAddresses(theme));

const HOME = 'home';
const PICKUP = 'pickup';

const MyAddresses = observer((props) => {
  const classes = useStyles();
  const { t } = useTranslation(['common', 'components']);

  const { userStore, uiStore } = useStores();
  const { addresses } = userStore;
  const [defaultAddr, setDefaultAddr] = useState();
  const [tab, setTab] = useState(HOME);

  // the pickup point flow is offered only where the provider delivers, but saved
  // points must stay reachable anyway: without either, there are no tabs and the
  // whole address book is listed
  const pickupEnabled =
    isPickupPointCountry(uiStore.country) ||
    (addresses || []).some(isPickupPointAddress);

  // memoized: AddressList resyncs its draft list on every new array identity
  const visibleAddresses = useMemo(() => {
    const all = addresses || [];
    if (!pickupEnabled) {
      return all;
    }
    return tab === PICKUP
      ? all.filter(isPickupPointAddress)
      : all.filter((a) => !isPickupPointAddress(a));
  }, [addresses, tab, pickupEnabled]);

  const { addresses: listAddresses, loading: loadingPoints } = usePickupPoints(
    visibleAddresses,
    pickupEnabled && tab === PICKUP,
  );

  const hiddenAddresses = useMemo(
    () => toJS((addresses || []).filter((a) => !visibleAddresses.includes(a))),
    [addresses, visibleAddresses],
  );
  const restoreStored = (list) =>
    (list || []).map((a) => {
      if (!isPickupPointAddress(a) || !a.rndId) {
        return a;
      }
      const stored = (addresses || []).find((o) => o.rndId === a.rndId);
      return stored ? toJS(stored) : a;
    });

  const withHidden = (list) => {
    const all = restoreStored(list).concat(hiddenAddresses);
    return all
      .filter((a) => a.defaultAddress)
      .concat(all.filter((a) => !a.defaultAddress));
  };

  useEffect(() => {
    if (addresses) {
      //  find the default addr
      const defaultAddr = addresses.find((a) => a.defaultAddress);
      // select the default addr
      if (defaultAddr) {
        setDefaultAddr(defaultAddr.id);
      }
    }
  }, [addresses]);

  const handleDelete = (addr, newAddresses) => {
    uiStore.openModal(
      t('components:MyAddresses.confirmModal.text'),
      t('components:MyAddresses.confirmModal.title'),
      {
        icon: <Image name="warning"></Image>,
        isError: true,
        confirmLabel: t('components:MyAddresses.confirmModal.confirmButton'),
        cancelLabel: t('components:MyAddresses.confirmModal.cancelButton'),
        onCancel: () => {
          uiStore.closeModal();
        },
        onConfirm: () => {
          uiStore.closeModal();
          //addressStore.resetBindings();
          userStore.updateAddress(withHidden(newAddresses));
        },
      },
    );
  };

  const handleSubmit = (addr, newAddresses, cb) => {
    return new Promise((resolve, reject) => {
      userStore.updateAddress(withHidden(newAddresses), cb);
    });
  };

  const handleChangeDefault = (newAddresses) => {
    userStore.updateAddress(withHidden(newAddresses));
  };

  return (
    <div id={props.id + '-myaddress'} className={classes.root}>
      <h2 className={classes.sectionTitle}>
        {t('components:MyAddresses.title')}
      </h2>
      {pickupEnabled && (
        <div className={classes.tabsBar}>
          <Tabs value={tab} onChange={(evt, value) => setTab(value)}>
            {[HOME, PICKUP].map((key) => (
              <Tab
                key={key}
                value={key}
                label={t('components:MyAddresses.tabs.' + key)}
              />
            ))}
          </Tabs>
        </div>
      )}
      {pickupEnabled && tab === PICKUP && loadingPoints ? (
        <Progress variant="medium" />
      ) : pickupEnabled && tab === PICKUP && visibleAddresses.length === 0 ? (
        <p className={classes.emptyList}>
          {t('components:MyAddresses.noPickupPoints')}
        </p>
      ) : (
        <AddressList
          id={'myAddress'}
          value={defaultAddr}
          addresses={listAddresses}
          onDelete={handleDelete}
          onSubmit={handleSubmit}
          onChangeDefault={handleChangeDefault}
          onEdit={props.onEdit}
          actionsEnabled={props.actionsEnabled}
          // pickup points are added from the subscription, not from here
          hideAddNew={pickupEnabled && tab === PICKUP}
        />
      )}
    </div>
  );
});

export default MyAddresses;
